import type { UiTheme } from "../api/auth";

/**
 * UI theme (V13): the user's choice lives on their account (`user.theme`) so it
 * follows them across browsers; this module applies it to the document and
 * mirrors it in localStorage so the very first paint after a reload already
 * has the right colours (no light flash before `/auth/me` answers).
 *
 * `styles.css` styles dark mode through `[data-theme="dark"]` on the root
 * element; light is the absence of the attribute. `SYSTEM` follows
 * `prefers-color-scheme` live.
 */

export const THEME_STORAGE_KEY = "bkm.theme";
export const UI_THEMES: readonly UiTheme[] = ["LIGHT", "DARK", "SYSTEM"];

const DARK_QUERY = "(prefers-color-scheme: dark)";

let current: UiTheme = "SYSTEM";
let mediaList: MediaQueryList | null = null;

function isUiTheme(v: unknown): v is UiTheme {
  return v === "LIGHT" || v === "DARK" || v === "SYSTEM";
}

/** The last theme stored in this browser; SYSTEM when nothing (or garbage) is stored. */
export function getStoredTheme(): UiTheme {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return isUiTheme(v) ? v : "SYSTEM";
  } catch {
    return "SYSTEM";
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

/** What actually gets painted for a preference. */
export function resolveTheme(pref: UiTheme, prefersDark = systemPrefersDark()): "light" | "dark" {
  if (pref === "DARK") return "dark";
  if (pref === "LIGHT") return "light";
  return prefersDark ? "dark" : "light";
}

function paint() {
  const root = document.documentElement;
  if (resolveTheme(current) === "dark") root.setAttribute("data-theme", "dark");
  else root.removeAttribute("data-theme");
}

function onSystemChange() {
  if (current === "SYSTEM") paint();
}

/** Applies a preference to the document and remembers it for the next reload. */
export function applyTheme(pref: UiTheme): void {
  current = pref;
  try { localStorage.setItem(THEME_STORAGE_KEY, pref); } catch { /* private mode etc. */ }
  if (mediaList === null && typeof window !== "undefined" && typeof window.matchMedia === "function") {
    mediaList = window.matchMedia(DARK_QUERY);
    mediaList.addEventListener("change", onSystemChange);
  }
  paint();
}

/** The preference currently applied. */
export function currentTheme(): UiTheme {
  return current;
}
